import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import { Box, Skeleton, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useCommonApi } from '../../api';
import { DealerOnly, dashboardCardSx } from '../common';

interface TopMerchantStatisticWidgetProps {
  topMerchantName: string;
  numberOfSolvedAccidents: number;
  isLoading?: boolean;
}

const pluralizeAccidents = (count: number) => {
  if (count === 1) return 'nehoda';
  if (count >= 2 && count <= 4) return 'nehody';
  return 'nehod';
};

const TopMerchantStatisticWidget = ({
  topMerchantName,
  numberOfSolvedAccidents,
  isLoading
}: TopMerchantStatisticWidgetProps) => (
  <Box
    sx={{ ...dashboardCardSx, height: 1, display: 'flex', flexDirection: 'column', gap: 3, padding: { xs: 2, md: 3 } }}
  >
    <Stack direction="row" alignItems="center" gap={1.5}>
      <Box
        sx={{
          display: 'grid',
          placeItems: 'center',
          width: 40,
          height: 40,
          borderRadius: 2,
          color: 'warning.dark',
          backgroundColor: 'rgba(252, 163, 17, 0.16)'
        }}
      >
        <EmojiEventsOutlinedIcon />
      </Box>
      <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.3 }}>
        Nejlepší obchodník tento měsíc
      </Typography>
    </Stack>
    {isLoading ? (
      <Stack spacing={1} sx={{ mt: 'auto' }}>
        <Skeleton variant="rounded" width="70%" height={24} />
        <Skeleton variant="rounded" width={96} height={48} />
      </Stack>
    ) : topMerchantName ? (
      <Stack spacing={0.5} sx={{ mt: 'auto' }}>
        <Typography fontWeight={600} sx={{ fontSize: '1.125rem' }}>
          {topMerchantName}
        </Typography>
        <Stack direction="row" alignItems="baseline" gap={1}>
          <Typography sx={{ fontSize: '3rem', fontWeight: 700, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
            {numberOfSolvedAccidents}
          </Typography>
          <Typography color="text.secondary" fontWeight={500}>
            {pluralizeAccidents(numberOfSolvedAccidents)}
          </Typography>
        </Stack>
      </Stack>
    ) : (
      <Typography color="text.secondary" sx={{ mt: 'auto' }}>
        Tento měsíc zatím nikdo nehodu nevyřešil.
      </Typography>
    )}
  </Box>
);

const TopMerchantStatisticWidgetConnectedContent = () => {
  const { data: dashboardStatisticsData, isLoading } = useQuery(useCommonApi().getDashboardStatistics());

  return (
    <TopMerchantStatisticWidget
      isLoading={isLoading}
      topMerchantName={dashboardStatisticsData?.dealerOfTheMonth?.fullName ?? ''}
      numberOfSolvedAccidents={dashboardStatisticsData?.dealerOfTheMonth?.count ?? 0}
    />
  );
};

export const TopMerchantStatisticWidgetConnected = () => (
  <DealerOnly>
    <TopMerchantStatisticWidgetConnectedContent />
  </DealerOnly>
);
